import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Sketch toolbar settings — user-configurable rail layout and Sketch-mode
 * drawing behavior.
 *
 * - `groupingEnabled`: flyout group menus ON (SketchUp large-toolset style)
 *   or OFF (every tool as one sorted, scrollable icon list).
 * - `planeFollowEnabled`: Sketch-style plane following — the active drawing
 *   plane silently follows the face under the cursor (empty-space strokes
 *   continue on the last face used). OFF pins empty-space drawing to the
 *   drawing axes / world ground (AutoCAD-style UCS drafting); faces are
 *   drawn on only through direct picks.
 * - `groupOrder`: tool family order. Reordered with the shared panel shell's
 *   pointer drag (@suite/panels GenericToolbar sortable rows); the shell
 *   reports from/to indexes, this store persists the resulting sequence.
 *
 * Persisted locally (settings, not document state) with the shared zustand
 * persist middleware.
 */

export const SKETCH_TOOLBAR_GROUP_ORDER = [
    'selectTransform',
    'draw',
    'model',
    'measure',
    'solid',
    'sandbox',
    'view',
    'ucs',
    'standardViews',
] as const;

interface SketchToolbarState {
    groupingEnabled: boolean;
    groupOrder: string[];
    /** Pixel width of the expanded two-column rail; null = compact rail. */
    railExpandedWidth: number | null;
    /** Sketch-style plane following (sticky drawing plane). Default ON. */
    planeFollowEnabled: boolean;
    toggleGrouping: () => void;
    setGrouping: (enabled: boolean) => void;
    setPlaneFollow: (enabled: boolean) => void;
    /** Persist a complete group sequence (shell drag-reorder result). */
    setGroupOrder: (order: string[]) => void;
    resetOrder: () => void;
    /** Persist a rail width from the viewport-edge resize drag. */
    setRailExpandedWidth: (width: number | null) => void;
}

export const useSketchToolbarStore = create<SketchToolbarState>()(
    persist(
        (set) => ({
            groupingEnabled: true,
            groupOrder: [...SKETCH_TOOLBAR_GROUP_ORDER],
            railExpandedWidth: null,
            planeFollowEnabled: true,

            toggleGrouping: () => set((s) => ({ groupingEnabled: !s.groupingEnabled })),
            setGrouping: (enabled) => set({ groupingEnabled: enabled }),
            setPlaneFollow: (enabled) => set({ planeFollowEnabled: enabled }),

            setGroupOrder: (order) => set({ groupOrder: [...order] }),

            resetOrder: () => set({ groupOrder: [...SKETCH_TOOLBAR_GROUP_ORDER] }),

            setRailExpandedWidth: (width) => set({ railExpandedWidth: width }),
        }),
        {
            name: 'yap-sketch-toolbar',
            version: 1,
            // v1 — the rail families were re-categorized (Navigation/Camera/
            // Modify/Construction became Select & Transform / Measure &
            // Annotate / View & Camera). A persisted pre-v1 order references
            // group ids that no longer exist and would scramble the new
            // canonical sequence, so it resets; the other settings carry over.
            migrate: (state) => {
                const saved = state as Partial<SketchToolbarState> | undefined;
                return {
                    groupingEnabled: saved?.groupingEnabled ?? true,
                    groupOrder: [...SKETCH_TOOLBAR_GROUP_ORDER],
                    railExpandedWidth: saved?.railExpandedWidth ?? null,
                    planeFollowEnabled: saved?.planeFollowEnabled ?? true,
                };
            },
            partialize: (state) => ({
                groupingEnabled: state.groupingEnabled,
                groupOrder: state.groupOrder,
                railExpandedWidth: state.railExpandedWidth,
                planeFollowEnabled: state.planeFollowEnabled,
            }),
        }
    )
);
